import React from 'react';
import { Callout, Hint } from '../../components/ui';
import { InfoIcon, WarningIcon } from '../../components/ui/Icons';
import { formatDateTime } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import type { EFormSubmissionApproval } from './useData';

function formatWhen(value?: string | null) {
  if (!value) return null;
  return formatDateTime(value) || null;
}

function noticeMeta(
  notice: NonNullable<EFormSubmissionApproval['notice']>,
): string {
  return [
    notice.byUserName,
    formatWhen(notice.at),
    notice.stepLabel ? `step “${notice.stepLabel}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function formatApprovalNoticeHint(
  approval: Pick<EFormSubmissionApproval, 'notice' | 'title' | 'currentStepLabel' | 'status'>,
): string | undefined {
  const notice = approval.notice;
  if (!notice) return undefined;
  const meta = noticeMeta(notice);
  const lines: string[] = [notice.summary];
  if (meta) lines.push(meta);
  if (notice.notes) lines.push(`Notes: ${notice.notes}`);
  else if (notice.kind === 'returned' || notice.kind === 'rejected') {
    lines.push('No comments were provided.');
  }
  if (notice.kind === 'returned') {
    lines.push(
      'Fix the source record, then use Resubmit for approval to send it back to the office.',
    );
  } else if (notice.kind === 'rejected') {
    lines.push(
      'This submission was rejected. Edit the draft if needed, then submit again to start a new approval request.',
    );
  } else if (notice.kind === 'cancelled') {
    lines.push('This approval was cancelled. You can submit again when ready.');
  } else if (notice.kind === 'overdue') {
    lines.push('An approval step is past its due time. Follow up with the assignee if needed.');
  }
  if (approval.status === 'PENDING' && approval.currentStepLabel) {
    lines.push(`Current step: ${approval.currentStepLabel}`);
  }
  return lines.join('\n');
}

function ApprovalNoticeHintBody({
  approval,
}: {
  approval: Pick<EFormSubmissionApproval, 'notice' | 'currentStepLabel' | 'status'>;
}) {
  const notice = approval.notice;
  if (!notice) return null;
  const meta = noticeMeta(notice);
  const guidance =
    notice.kind === 'returned'
      ? 'Fix the source record, then use Resubmit for approval to send it back to the office.'
      : notice.kind === 'rejected'
        ? 'This submission was rejected. Edit the draft if needed, then submit again to start a new approval request.'
        : notice.kind === 'cancelled'
          ? 'This approval was cancelled. You can submit again when ready.'
          : notice.kind === 'overdue'
            ? 'An approval step is past its due time. Follow up with the assignee if needed.'
            : null;

  return (
    <div className="space-y-1.5 text-left">
      {meta ? <p className="text-[11px] opacity-90">{meta}</p> : null}
      {notice.notes ? (
        <p className="italic leading-snug">“{notice.notes}”</p>
      ) : notice.kind === 'returned' || notice.kind === 'rejected' ? (
        <p className="opacity-90">
          No comments were provided with the {notice.kind === 'returned' ? 'return' : 'rejection'}.
        </p>
      ) : null}
      {guidance ? <p className="leading-snug opacity-95">{guidance}</p> : null}
      {approval.status === 'PENDING' && approval.currentStepLabel ? (
        <p className="text-[11px] opacity-90">Current step: {approval.currentStepLabel}</p>
      ) : null}
    </div>
  );
}

/** Warning / info icon with `Hint` panel when the approval has a notable notice. */
export function ApprovalNoticeIcon({
  approval,
  className,
}: {
  approval: Pick<EFormSubmissionApproval, 'notice' | 'title' | 'currentStepLabel' | 'status'>;
  className?: string;
}) {
  const notice = approval.notice;
  if (!notice) return null;

  const toneClass =
    notice.tone === 'danger'
      ? 'text-red-600 dark:text-red-400'
      : notice.tone === 'warning'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-sky-600 dark:text-sky-400';

  const Icon = notice.tone === 'info' ? InfoIcon : WarningIcon;
  const aria = formatApprovalNoticeHint(approval)?.replace(/\n/g, '. ') ?? notice.summary;

  return (
    <Hint
      label={notice.summary}
      content={<ApprovalNoticeHintBody approval={approval} />}
      side="top"
      delay={180}
      maxWidth={340}
      triggerClassName={cn('inline-flex shrink-0', toneClass, className)}
    >
      <span
        className="inline-flex"
        aria-label={aria}
        onClick={(e) => e.stopPropagation()}
      >
        <Icon className="w-3.5 h-3.5" />
      </span>
    </Hint>
  );
}

/**
 * Full banner matching Approvals detail “Returned for changes” (and reject/cancel variants).
 */
export function ApprovalNoticeBanner({
  approval,
  className,
}: {
  approval: Pick<EFormSubmissionApproval, 'notice' | 'id'>;
  className?: string;
}) {
  const notice = approval.notice;
  if (!notice) return null;

  const meta = noticeMeta(notice);

  const title =
    notice.kind === 'returned'
      ? 'Returned for changes'
      : notice.kind === 'rejected'
        ? 'Rejected'
        : notice.kind === 'cancelled'
          ? 'Approval cancelled'
          : notice.summary;

  const guidance =
    notice.kind === 'returned'
      ? (
          <>
            Fix the source record, then use <strong>Resubmit for approval</strong> to send it back to
            the office.
          </>
        )
      : notice.kind === 'rejected'
        ? (
            <>
              This submission was rejected. Edit the draft if needed, then submit again to start a new
              approval request.
            </>
          )
        : notice.kind === 'cancelled'
          ? <>This approval was cancelled. You can submit again when ready.</>
          : notice.kind === 'overdue'
            ? <>An approval step is past its due time. Follow up with the assignee if needed.</>
            : null;

  return (
    <Callout
      variant={notice.tone}
      title={title}
      className={className}
      icon={notice.tone === 'info' ? <InfoIcon className="h-5 w-5" /> : <WarningIcon className="h-5 w-5" />}
    >
      {meta ? <p>{meta}</p> : null}
      {notice.notes ? (
        <blockquote
          className={cn(
            'mt-2 rounded-lg border-l-4 px-3 py-2 text-sm italic',
            notice.tone === 'danger'
              ? 'border-red-400/80 bg-red-100/60 dark:border-red-500/50 dark:bg-red-950/40'
              : notice.tone === 'warning'
                ? 'border-amber-400/80 bg-amber-100/60 dark:border-amber-500/50 dark:bg-amber-950/40'
                : 'border-sky-400/80 bg-sky-100/60 dark:border-sky-500/50 dark:bg-sky-950/40',
          )}
        >
          “{notice.notes}”
        </blockquote>
      ) : notice.kind === 'returned' || notice.kind === 'rejected' ? (
        <p className="mt-2">No comments were provided with the {notice.kind === 'returned' ? 'return' : 'rejection'}.</p>
      ) : null}
      {guidance ? <p className="mt-2">{guidance}</p> : null}
    </Callout>
  );
}
